import React, { useState, useCallback, useMemo } from 'react';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check';
import Target from 'lucide-react/dist/esm/icons/target';
import Settings from 'lucide-react/dist/esm/icons/settings';
import Sun from 'lucide-react/dist/esm/icons/sun';
import Moon from 'lucide-react/dist/esm/icons/moon';
import { useUIStore, useThemeStore, useOrgStore } from '../../../stores';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permissions } from '../../../config/permissions';
import type { AppModule } from '../../../types';
import type { LucideIcon } from 'lucide-react';

interface ModuleItem {
  id: AppModule;
  label: string;
  icon: LucideIcon;
  permission?: string;
  tooltip?: string;
}

const MODULE_TO_USER_MODULE: Record<string, string> = {
  'financial-forensics': 'financial-forensics',
  'performance': 'performance',
  'aml-compliance': 'aml',
  'sales': 'sales',
};

const ALL_MODULES: ModuleItem[] = [
  {
    id: 'financial-forensics',
    label: 'Financial Forensics',
    icon: DollarSign,
  },
  {
    id: 'performance',
    label: 'Performance',
    icon: BarChart3,
  },
  {
    id: 'aml-compliance',
    label: 'AML Compliance',
    icon: ShieldCheck,
    permission: Permissions.AML_ACCESS,
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: Target,
    permission: Permissions.SALES_ACCESS,
  },
];

const ALL_BOTTOM_MODULES: ModuleItem[] = [
  {
    id: 'org-management',
    label: 'Settings',
    icon: Settings,
    permission: Permissions.ORG_MEMBERS_VIEW,
  },
];

const AppSidebar: React.FC = () => {
  const { activeModule, setActiveModule } = useUIStore();
  const { resolvedMode, toggleMode } = useThemeStore();
  const userContext = useOrgStore((s) => s.userContext);
  const contextLoading = useOrgStore((s) => s.contextLoading);
  const { hasPermission } = usePermissions();
  const [flickeringId, setFlickeringId] = useState<string | null>(null);

  const userModules = userContext?.user_modules || [];

  const MODULES = useMemo(() => {
    if (!userContext) return [];
    if (userContext.system_role === 'platform_admin') {
      return ALL_MODULES;
    }
    return ALL_MODULES.filter((m) => {
      const userMod = MODULE_TO_USER_MODULE[m.id];
      if (userMod) return userModules.includes(userMod);
      return !m.permission || hasPermission(m.permission as any);
    });
  }, [userContext, userModules, hasPermission]);

  const BOTTOM_MODULES = useMemo(() => {
    if (!userContext) return [];
    return ALL_BOTTOM_MODULES.filter((m) => !m.permission || hasPermission(m.permission as any));
  }, [userContext, hasPermission]);

  const handleModuleClick = useCallback((moduleId: AppModule) => {
    setFlickeringId(moduleId);
    setActiveModule(moduleId);
    setTimeout(() => setFlickeringId(null), 350);
  }, [setActiveModule]);

  return (
    <aside className="fixed top-[60px] left-0 w-[60px] h-[calc(100vh-60px)] bg-surface-page border-r border-subtle z-[51] flex flex-col items-center pt-3 transition-all duration-300">
      {/* Module Navigation */}
      <nav className="flex flex-col items-center gap-1 w-full px-1.5">
        {MODULES.map((module) => {
          const isActive = activeModule === module.id;
          const isFlickering = flickeringId === module.id;
          const Icon = module.icon;

          return (
            <div key={module.id} className="relative w-full group">
              <button
                onClick={() => handleModuleClick(module.id)}
                className={`
                  relative w-full flex items-center justify-center h-11 rounded-lg cursor-pointer
                  transition-all duration-200 border-none outline-none
                  ${isFlickering ? 'animate-digital-flicker' : ''}
                  ${isActive
                    ? 'bg-status-success/15 text-status-success'
                    : 'bg-transparent text-content-muted hover:bg-surface-primary hover:text-content-secondary'
                  }
                `}
              >
                {/* Active indicator bar */}
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-status-success rounded-r-full" />
                )}

                <Icon size={20} strokeWidth={isActive ? 2.2 : 1.8} />
              </button>

              {/* Tooltip - outside button so it doesn't inherit flicker animation */}
              <span className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-surface-page border border-default rounded-lg text-xs font-medium text-content whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 delay-0 group-hover:delay-[1000ms] z-tooltip shadow-xl">
                {module.label}
                {module.tooltip && (
                  <span className="block text-[10px] text-content-muted mt-0.5">{module.tooltip}</span>
                )}
              </span>
            </div>
          );
        })}
      </nav>

      {/* Bottom: Theme toggle + Settings */}
      <nav className="mt-auto flex flex-col items-center gap-1 w-full px-1.5 pb-4">
        {/* Theme Mode Toggle */}
        <div className="relative w-full group">
          <button
            onClick={toggleMode}
            className="relative w-full flex items-center justify-center h-11 rounded-lg cursor-pointer transition-all duration-200 border-none outline-none bg-transparent text-content-muted hover:bg-surface-secondary hover:text-content-secondary"
          >
            {resolvedMode === 'dark'
              ? <Sun size={18} strokeWidth={1.8} />
              : <Moon size={18} strokeWidth={1.8} />
            }
          </button>
          <span className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-surface-elevated border border-default rounded-lg text-xs font-medium text-content whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 delay-0 group-hover:delay-[1000ms] z-tooltip shadow-lg">
            {resolvedMode === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
          </span>
        </div>
        <div className="w-8 h-px bg-border-subtle mb-1" />
        {BOTTOM_MODULES.map((module) => {
          const isActive = activeModule === module.id;
          const isFlickering = flickeringId === module.id;
          const Icon = module.icon;

          return (
            <div key={module.id} className="relative w-full group">
              <button
                onClick={() => handleModuleClick(module.id)}
                className={`
                  relative w-full flex items-center justify-center h-11 rounded-lg cursor-pointer
                  transition-all duration-200 border-none outline-none
                  ${isFlickering ? 'animate-digital-flicker' : ''}
                  ${isActive
                    ? 'bg-status-success/15 text-status-success'
                    : 'bg-transparent text-content-muted hover:bg-surface-primary hover:text-content-secondary'
                  }
                `}
              >
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-status-success rounded-r-full" />
                )}
                <Icon size={20} strokeWidth={isActive ? 2.2 : 1.8} />
              </button>

              <span className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-surface-page border border-default rounded-lg text-xs font-medium text-content whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 delay-0 group-hover:delay-[1000ms] z-tooltip shadow-xl">
                {module.label}
                {module.tooltip && (
                  <span className="block text-[10px] text-content-muted mt-0.5">{module.tooltip}</span>
                )}
              </span>
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default React.memo(AppSidebar);
